import { Component } from '@theme/component';

if (!customElements.get('dynamic-collections-grid')) {
  class DynamicCollectionsGrid extends Component {
    /** @type {number} */
    totalSlides = 0;

    /** @type {number} */
    currentSlideIndex = 1;

    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();

      this.#reInitSlider();
      this.#initChangeMedia();
      this.#setupPaginationListeners();
      this.#preloadHoverImages();
    }

    /**
     * Précharge les images de hover (masquées) pour un affichage instantané au survol.
     */
    #preloadHoverImages() {
      const warmup = () => {
        const images = this.querySelectorAll('.dynamic-collections-grid__item-main-media img');

        images.forEach((img) => {
          const preload = new Image();
          if (img.getAttribute('sizes')) preload.sizes = img.getAttribute('sizes') ?? '';
          if (img.getAttribute('srcset')) preload.srcset = img.getAttribute('srcset') ?? '';
          preload.src = img.getAttribute('src') ?? '';
        });
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(warmup, { timeout: 2000 });
      } else {
        setTimeout(warmup, 500);
      }
    }

    /**
     * Réinitialise le slider après un morph ou un chargement
     */
    #reInitSlider() {
      // Trouver le slider-component dans le contenu
      const slider = /** @type {any} */ (this.querySelector('slider-component'));
      if (slider && typeof slider.reInit === 'function') {
        slider.reInit();
      }

      // Initialiser la pagination
      this.#initPagination();
    }

    /**
     * Retourne les slides visibles (les items au-delà de la limite sont masqués en CSS)
     * @returns {Element[]}
     */
    #getVisibleSlides() {
      return [...this.querySelectorAll('.dynamic-collections-grid__slide')].filter(
        (slide) => getComputedStyle(slide).display !== 'none'
      );
    }

    /**
     * Initialise la pagination
     */
    #initPagination() {
      const slides = this.#getVisibleSlides();
      this.totalSlides = slides.length;
      this.currentSlideIndex = 1;
      this.#updatePaginationIndicator();
    }

    /**
     * Configure les écouteurs pour les boutons de pagination
     */
    #setupPaginationListeners() {
      const prevBtn = /** @type {HTMLElement | undefined} */ (this.refs.prevBtn);
      const nextBtn = /** @type {HTMLElement | undefined} */ (this.refs.nextBtn);

      if (prevBtn) {
        prevBtn.addEventListener('click', () => this.#prevSlide());
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => this.#nextSlide());
      }

      // Écouter les changements de slide du slider-component
      const slider = /** @type {any} */ (this.querySelector('slider-component'));
      if (slider) {
        slider.addEventListener('slideChange', (/** @type {CustomEvent} */ event) => {
          if (event.detail && typeof event.detail.index === 'number') {
            this.currentSlideIndex = event.detail.index + 1;
            this.#updatePaginationIndicator();
          }
        });
      }
    }

    /**
     * Va au slide précédent
     */
    #prevSlide() {
      const slider = /** @type {any} */ (this.querySelector('slider-component'));
      if (slider && typeof slider.scrollPrev === 'function') {
        slider.scrollPrev();
      }

      let newIndex = this.currentSlideIndex - 1;
      if (newIndex < 1) newIndex = this.totalSlides;
      this.currentSlideIndex = newIndex;
      this.#updatePaginationIndicator();
      this.#scrollToSlide(newIndex - 1);
    }

    /**
     * Va au slide suivant
     */
    #nextSlide() {
      const slider = /** @type {any} */ (this.querySelector('slider-component'));
      if (slider && typeof slider.scrollNext === 'function') {
        slider.scrollNext();
      }

      let newIndex = this.currentSlideIndex + 1;
      if (newIndex > this.totalSlides) newIndex = 1;
      this.currentSlideIndex = newIndex;
      this.#updatePaginationIndicator();
      this.#scrollToSlide(newIndex - 1);
    }

    /**
     * Scroll vers un slide spécifique
     * @param {number} index - L'index du slide (0-based)
     */
    #scrollToSlide(index) {
      const slides = this.#getVisibleSlides();
      const slide = slides[index];
      if (slide) {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }

    /**
     * Met à jour l'indicateur de pagination
     */
    #updatePaginationIndicator() {
      const currentIndex = /** @type {HTMLElement | undefined} */ (this.refs.currentIndex);

      if (currentIndex) {
        currentIndex.textContent = String(this.currentSlideIndex);
      }
    }

    /**
     * Load video sources if needed.
     * @param {Element} mediaItem
     */
    #loadMediaIfNeeded(mediaItem) {
      const video = mediaItem.querySelector('video');
      if (!video) return;

      const sources = video.querySelectorAll('source[data-src]');
      if (sources.length === 0) return;

      let needsLoad = false;

      for (const source of sources) {
        const dataSrc = source.getAttribute('data-src');

        if (dataSrc && !source.getAttribute('src')) {
          source.setAttribute('src', dataSrc);
          needsLoad = true;
        }
      }

      if (needsLoad) {
        video.load();
      }
    }

    /**
     * Play video in the provided media item.
     * @param {Element} mediaItem
     */
    #playMediaVideo(mediaItem) {
      const video = /** @type {HTMLVideoElement | null} */ (mediaItem.querySelector('video'));
      if (!video) return;

      const play = () => {
        video.play().catch(() => { });
      };

      if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        play();
        return;
      }

      video.addEventListener('canplay', play, { once: true });
    }

    #initChangeMedia() {
      const slides = this.querySelectorAll('.dynamic-collections-grid__slide');
      const mainMedia = /** @type {HTMLElement | undefined} */ (this.refs.mainMedia);

      if (slides.length > 0 && mainMedia) {
        slides.forEach((slide) => {
          ['click', 'mouseover'].forEach((event) => {
            slide.addEventListener(event, () => {
              // Option "masquer le grand média sur mobile" : rien à mettre à jour tant qu'il est caché
              if (mainMedia.offsetParent === null) return;

              const media = slide.querySelector('.dynamic-collections-grid__item-main-media');

              if (mainMedia && media) {
                mainMedia.innerHTML = media.innerHTML;

                this.#loadMediaIfNeeded(mainMedia);
                this.#playMediaVideo(mainMedia);
              } else if (mainMedia) {
                mainMedia.innerHTML = '';
              }
            });
          });
        });
      }
    }
  }

  customElements.define('dynamic-collections-grid', DynamicCollectionsGrid);
}